import { useState } from "react";
import Plus from "../../assets/icons/Plus";
import Edit from "../../assets/icons/Edit";
import Delete from "../../assets/icons/Delete";
import { Stop } from "../../assets/icons/Stop";
import DefaultButton from "../default-button/DefaultButton";
import InputField from "../input-field/InputField";
import DateRangeModal from "./DateRangeModal";
import Calendar from "../../assets/icons/Calendar";
import { Lesson } from "./AttendanceLessonsList";

export interface AdminLesson extends Lesson {
    status?: 'upcoming' | 'in_progress' | 'completed';
}

interface AttendanceAdminLessonsListProps {
    lessons: AdminLesson[];
    selectedLesson: AdminLesson | undefined;
    onLessonSelect?: (lesson: AdminLesson | undefined) => void;
    onCreateLesson?: () => void;
    onStartAttendance?: (lessonId: number) => void;
    onStopAttendance?: (lessonId: number) => void;
    onStudentList?: (lessonId: number) => void;
    onDeleteLesson?: (lessonId: number) => void;
    onEditLesson?: (lessonId: number) => void;
}

const Buttons = ({ lessonId, onEdit, onDelete }: { lessonId: number; onEdit: () => void; onDelete: () => void }) => {
    return (
        <div className="flex flex-col items-center justify-center gap-1">
            <div className="flex items-center justify-center border-none rounded-md bg-white">
                <DefaultButton 
                    Icon={Edit}
                    additionalStyle="p-[2px] text-valley-gray rounded-md text-[13px] font-semibold border-none bg-valley-blue/20"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                />
            </div>
            <div className="flex items-center justify-center border-none rounded-md bg-white">
                <DefaultButton 
                    Icon={Delete}
                    additionalStyle="p-[2px] text-valley-red rounded-md text-[13px] font-semibold border-none bg-valley-red/20"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                />
            </div>
        </div>
    );
};

export default function AttendanceAdminLessonsList({
    lessons,
    selectedLesson,
    onLessonSelect,
    onCreateLesson,
    onStartAttendance,
    onStopAttendance,
    onStudentList,
    onDeleteLesson,
    onEditLesson,
}: AttendanceAdminLessonsListProps) {
    const [searchValue, setSearchValue] = useState("");
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const handleSort = (key: string) => {
        setSortConfig(prev => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const filteredLessons = lessons.filter(lesson => {
        const matchesSearch = !searchValue || 
            lesson.date.toLowerCase().includes(searchValue.toLowerCase());
        
        if (dateFrom && dateTo) {
            const lessonDate = new Date(lesson.date.split('.').reverse().join('-'));
            return lessonDate >= dateFrom && lessonDate <= dateTo;
        }
        
        return matchesSearch;
    });

    const sortedLessons = [...filteredLessons].sort((a, b) => {
        if (!sortConfig) return 0;
        
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
            case 'date':
                aValue = new Date(a.date.split('.').reverse().join('-'));
                bValue = new Date(b.date.split('.').reverse().join('-'));
                break;
            case 'score':
                aValue = a.averageScore;
                bValue = b.averageScore;
                break;
            default:
                return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ columnKey }: { columnKey: string }) => {
        if (sortConfig?.key !== columnKey) return null;
        return (
            <span className="ml-1">
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const handleDateRangeApply = (from: Date | null, to: Date | null) => {
        setDateFrom(from);
        setDateTo(to);
    };

    const hasDateFilter = dateFrom && dateTo;
    const dateFilterText = hasDateFilter
        ? `${dateFrom.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${dateTo.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
        : '';

    return (
        <div className="bg-white rounded-lg p-4 flex flex-col h-full min-h-0 overflow-hidden">
            {/* Заголовок */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0 flex-wrap gap-2">
                <div className="text-xl md:text-2xl font-semibold">
                    Посещаемость курса
                </div>
                <div>
                    <DefaultButton 
                        buttonText="Создать урок" 
                        Icon={Plus} 
                        additionalStyle="text-valley-dark-green border border-valley-dark-green rounded-lg py-2 px-2 md:px-3 text-sm md:text-base whitespace-nowrap" 
                        onClick={onCreateLesson} 
                    />
                </div>
            </div>

            {/* Поиск и фильтрация по дате */}
            <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <InputField
                            type="text"
                            placeholder="Поиск"
                            value={searchValue}
                            handleChange={(e) => setSearchValue(e.target.value)}
                            additionalClassName="!bg-[#F0F3FA] py-2 px-4 rounded-lg w-full"
                        />
                    </div>
                    <DefaultButton
                        buttonText="Указать даты"
                        Icon={Calendar}
                        additionalStyle="text-valley-main border border-valley-main rounded-lg py-2 px-3 whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                        onClick={() => setIsDateRangeModalOpen(true)}
                    />
                </div>
                {hasDateFilter && (
                    <div className="flex items-center gap-2 text-sm text-valley-gray flex-wrap">
                        <span>Период: {dateFilterText}</span>
                        <button
                            onClick={() => handleDateRangeApply(null, null)}
                            className="text-valley-red hover:text-valley-red/80 text-lg leading-none"
                            title="Очистить фильтр"
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>

            {/* Таблица уроков */}
            <div className="flex-1 overflow-auto min-h-0">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse min-w-[500px]">
                        <thead>
                            <tr className="border-b border-valley-background-blue text-valley-gray text-xs md:text-sm font-medium">
                                <th 
                                    className="text-left py-2 px-2 md:px-4 cursor-pointer"
                                    onClick={() => handleSort('date')}
                                >
                                    Дата <SortIcon columnKey="date" />
                                </th>
                                <th 
                                    className="text-left py-2 px-2 md:px-4 cursor-pointer"
                                    onClick={() => handleSort('score')}
                                >
                                    Средний балл <SortIcon columnKey="score" />
                                </th>
                                <th className="text-center py-2 px-2 md:px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedLessons.map((lesson) => {
                                const isSelected = selectedLesson?.id === lesson.id;
                                const status = lesson.status || 'upcoming';

                                return (
                                    <tr 
                                        key={lesson.id} 
                                        className={`border-b border-valley-background-blue ${
                                            isSelected ? 'bg-valley-main text-white' : 'text-valley-black hover:bg-valley-background-blue cursor-pointer'
                                        }`}
                                        onClick={() => onLessonSelect?.(lesson)}
                                    >
                                        <td className="py-2 px-2 md:px-4 text-sm">{lesson.date}</td>
                                        <td className="py-2 px-2 md:px-4 text-sm">{lesson.averageScore}</td>
                                    <td className="py-2 px-2 md:px-4">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-end gap-2">
                                            <div className="flex flex-col gap-1 items-end w-full sm:w-auto">
                                                {status === 'upcoming' && (
                                                    <DefaultButton 
                                                        buttonText="Начать аттенданс" 
                                                        additionalStyle={`${
                                                            isSelected 
                                                                ? 'bg-white text-valley-dark-green border border-valley-dark-green' 
                                                                : 'bg-valley-dark-green text-white'
                                                        } rounded-lg py-1 px-2 md:px-3 text-xs md:text-[13px] font-semibold w-full sm:w-auto`} 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStartAttendance?.(lesson.id);
                                                        }}
                                                    />
                                                )}
                                                {status === 'in_progress' && (
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-xs md:text-[13px] font-semibold ${isSelected ? 'text-white' : 'text-valley-main'}`}>
                                                            Аттенданс идёт
                                                        </span>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onStopAttendance?.(lesson.id);
                                                            }}
                                                            className="p-1 bg-valley-red text-white rounded hover:bg-valley-red/80 flex items-center justify-center flex-shrink-0"
                                                        >
                                                            <Stop additionalStyle="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                                {status === 'completed' && (
                                                    <span className={`text-xs md:text-[13px] font-semibold ${isSelected ? 'text-white' : 'text-valley-gray'}`}>
                                                        Аттенданс завершён
                                                    </span>
                                                )}
                                                <DefaultButton 
                                                    buttonText="Список студентов" 
                                                    additionalStyle={`${
                                                        isSelected 
                                                            ? 'bg-white text-valley-main border border-valley-main' 
                                                            : 'text-valley-main bg-white border border-valley-main'
                                                    } rounded-lg py-1 px-2 md:px-3 text-xs md:text-[13px] font-semibold w-full sm:w-auto`} 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onStudentList?.(lesson.id);
                                                    }}
                                                />
                                            </div>
                                            <Buttons
                                                lessonId={lesson.id}
                                                onEdit={() => onEditLesson?.(lesson.id)}
                                                onDelete={() => onDeleteLesson?.(lesson.id)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Модалка выбора диапазона дат */}
            <DateRangeModal
                isOpen={isDateRangeModalOpen}
                onClose={() => setIsDateRangeModalOpen(false)}
                onApply={handleDateRangeApply}
                dateFrom={dateFrom}
                dateTo={dateTo}
            />
        </div>
    );
}

